import React, { useContext, useEffect, useRef, useState } from "react";
import "../../../styles/Trends.css";
import SelectContext from "../../../utils/Context/SelectContext";
import { useFetchQueDataList } from "../../../utils/hooks/UseFetchUrl";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { base_url } from "../../../utils/apiConfig";
import { Spin, Tooltip, Typography } from "antd";
import { Outlet } from "react-router-dom";
import useClientAdmin from "../../../utils/hooks/IsClientAdmin";
import { VerticalAlignBottomOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx-js-style";
import BarChart from "../../../components/chart-bars/BarChart";


const Trends = ({ storeData }) => {
  const location = useLocation();
  const queDataListId = storeData?.id;
  const context = useContext(SelectContext);
  const { selectedOption, setSelectedOption, setSelectedQueId } = context;
  const { data: queTypesDataList } = useFetchQueDataList(`/store/${queDataListId}/questionnaire_types_list`);
  const isClientAdmin = useClientAdmin();

  const [selectedId, setSelectedId] = useState(null);
  const [markingGraphData, setMarkingGraphData] = useState(null);
  const [markingTablesData, setMarkingTablesData] = useState(null);
  const [processedScoresData, setProcessedScoresData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { Title } = Typography;
  const prevId = useRef(null);
  const prevListId = useRef(null);

  useEffect(() => {
    if (queTypesDataList && queTypesDataList.length > 0) {
      const defaultId = queTypesDataList[0].id;
      setSelectedId(defaultId);
      setSelectedOption(defaultId.toString());
    }
  }, [queTypesDataList]);

  useEffect(() => {
    if (selectedId && queDataListId) {
      const fetchMarkingGraphData = async () => {
        setIsLoading(true);
        try {
          const authToken = localStorage.getItem("authToken");
          const response = await axios.get(
            `${base_url.protocol}://${base_url.hostname}:${base_url.port}${base_url.api_base_path}client/report/questionnaire_type/${selectedId}/store/${queDataListId}/marking_graph`,
            {
              headers: {
                Authorization: `token ${authToken}`,
                "Content-Type": "application/json",
              },
            }
          );
          setMarkingGraphData(response.data);
          const markingTableData = await axios.get(
            `${base_url.protocol}://${base_url.hostname}:${base_url.port}${base_url.api_base_path}client/report/questionnaire_type/${selectedId}/store/${queDataListId}/marking`,
            {
              headers: {
                Authorization: `token ${authToken}`,
                "Content-Type": "application/json",
              },
            }
          );
          setMarkingTablesData(markingTableData.data);
          const graphData = response.data;
          const groupedSections = markingTableData?.data.scores.reduce(
            (acc, item) => {
              if (!acc[item.section_name]) {
                acc[item.section_name] = {
                  section_name: item.section_name,
                  maxMarks: 0,
                  totalMarks: 0,
                };
              }
              const marksForQuestion = item.scores[0]?.marks || 0;
              acc[item.section_name].maxMarks += item.max_marks;
              acc[item.section_name].totalMarks += marksForQuestion;
              return acc;
            },
            {}
          );
          const processed = Object.values(groupedSections).map((section) => ({
            ...section,
            percentage:
              section.maxMarks > 0
                ? ((section.totalMarks / section.maxMarks) * 100).toFixed(2)
                : "0",
          }));
          setProcessedScoresData(processed);
        } catch (error) {
          console.log("Error fetching marking graph data:", error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchMarkingGraphData();
    }
  }, [selectedId, queDataListId]);

  const groupDataBySection = () => {
    if (!markingTablesData?.scores) return [];
    const groupedData = markingTablesData.scores.reduce((acc, score) => {
      const sectionSequence = score.section_sequence;
      if (!acc[sectionSequence]) {
        acc[sectionSequence] = [];
      }
      acc[sectionSequence].push(score);
      return acc;
    }, {});
    return Object.keys(groupedData).map((sectionSequence) => ({
      sectionSequence,
      scores: groupedData[sectionSequence],
    }));
  };
  const tablesData = groupDataBySection();
  const handleSelectChange = (event) => {
    const newSelectedId = parseInt(event.target.value, 10);
    setSelectedId(newSelectedId);
    setSelectedOption(event.target.value);
  };

  const getScoreColor = (scoreMarks, maxMarks) => {
    if (scoreMarks === null) {
      return "FFFFFF";
    }

    if (scoreMarks === 0) {
      return "F1717D";
    }

    if (scoreMarks === maxMarks) {
      return "8DC63F";
    }

    const percentage = scoreMarks / maxMarks;

    if (percentage >= 0.8) {
      return "C3E770";
    }

    if (percentage >= 0.6) {
      return "FFEB99";
    }

    if (percentage >= 0.4) {
      return "FFB380";
    }

    return "FFA07A";
  };

  const handleDownloadReport = () => {
    const workbook = XLSX.utils.book_new();

    const worksheetData = [];

    tablesData
      ?.filter((table) =>
        table.scores?.some((row) => row.max_marks !== 0)
      )
      .forEach((table) => {

        // SECTION TITLE
        worksheetData.push([
          table.scores?.[0]?.section_name || "NA",
        ]);

        // HEADER ROW
        const headers = [
          "Sr. no.",
          "Questions",
          "Max Marks",
          ...markingTablesData.audit_cycle,
        ];

        worksheetData.push(headers);

        // DATA ROWS
        table.scores
          ?.filter(
            (row) =>
              row.max_marks !== 0 ||
              row.scores?.some(
                (score) => score.marks > 0
              )
          )
          .filter(
            (row) =>
              row.scores?.length >=
              markingTablesData?.audit_cycle?.length
          )
          .filter(
            (row) =>
              !row.scores.every(
                (score) => score.marks === null
              )
          )
          .forEach((row) => {

            const rowData = [
              row.sequence,
              row.question_txt,
              row.max_marks,
            ];

            markingTablesData.audit_cycle.forEach(
              (_, cycleIndex) => {
                const scoreMarks =
                  row.scores?.[cycleIndex]?.marks !==
                    undefined
                    ? row.scores[cycleIndex].marks
                    : null;

                rowData.push(
                  scoreMarks !== null
                    ? scoreMarks
                    : ""
                );
              }
            );

            worksheetData.push(rowData);
          });

        // EMPTY SPACE BETWEEN TABLES
        worksheetData.push([]);
        worksheetData.push([]);
      });

    const worksheet =
      XLSX.utils.aoa_to_sheet(worksheetData);

    // COLUMN WIDTHS
    worksheet["!cols"] = [
      { wch: 10 },
      { wch: 70 },
      { wch: 15 },
      ...markingTablesData.audit_cycle.map(() => ({
        wch: 15,
      })),
    ];

    let currentRow = 0;

    tablesData
      ?.filter((table) =>
        table.scores?.some((row) => row.max_marks !== 0)
      )
      .forEach((table) => {

        // SECTION TITLE STYLE
        const sectionCell =
          XLSX.utils.encode_cell({
            r: currentRow,
            c: 0,
          });

        if (worksheet[sectionCell]) {
          worksheet[sectionCell].s = {
            font: {
              bold: true,
              sz: 14,
              color: { rgb: "000000" },
            },
          };
        }

        currentRow++;

        // HEADER STYLE
        for (
          let col = 0;
          col <
          3 +
          markingTablesData.audit_cycle.length;
          col++
        ) {
          const cellRef =
            XLSX.utils.encode_cell({
              r: currentRow,
              c: col,
            });

          if (worksheet[cellRef]) {
            worksheet[cellRef].s = {
              font: {
                bold: true,
              },

              alignment: {
                horizontal: "center",
                vertical: "center",
                wrapText: true,
              },

              fill: {
                fgColor: {
                  rgb: "EDEDED",
                },
              },

              border: {
                top: {
                  style: "thin",
                  color: { rgb: "D9D9D9" },
                },
                bottom: {
                  style: "thin",
                  color: { rgb: "D9D9D9" },
                },
                left: {
                  style: "thin",
                  color: { rgb: "D9D9D9" },
                },
                right: {
                  style: "thin",
                  color: { rgb: "D9D9D9" },
                },
              },
            };
          }
        }

        currentRow++;

        // DATA ROWS
        table.scores
          ?.filter(
            (row) =>
              row.max_marks !== 0 ||
              row.scores?.some(
                (score) => score.marks > 0
              )
          )
          .forEach((row, rowIndex) => {

            // zebra rows
            const rowBg =
              rowIndex % 2 === 0
                ? "FFFFFF"
                : "F2F2F2";

            // normal cells
            for (
              let col = 0;
              col <
              3 +
              markingTablesData.audit_cycle
                .length;
              col++
            ) {
              const cellRef =
                XLSX.utils.encode_cell({
                  r: currentRow,
                  c: col,
                });

              if (worksheet[cellRef]) {
                worksheet[cellRef].s = {
                  alignment: {
                    vertical: "center",
                    wrapText: true,
                    horizontal:
                      col === 1
                        ? "left"
                        : "center",
                  },

                  fill: {
                    fgColor: {
                      rgb: rowBg,
                    },
                  },

                  border: {
                    top: {
                      style: "thin",
                      color: { rgb: "D9D9D9" },
                    },
                    bottom: {
                      style: "thin",
                      color: { rgb: "D9D9D9" },
                    },
                    left: {
                      style: "thin",
                      color: { rgb: "D9D9D9" },
                    },
                    right: {
                      style: "thin",
                      color: { rgb: "D9D9D9" },
                    },
                  },
                };
              }
            }
            // ---------------
            // MAX MARKS COLUMN STYLE
            const maxMarksCellRef =
              XLSX.utils.encode_cell({
                r: currentRow,
                c: 2,
              });

            if (worksheet[maxMarksCellRef]) {
              worksheet[maxMarksCellRef].s = {
                alignment: {
                  horizontal: "center",
                  vertical: "center",
                },
                font: {
                  bold: true,
                  color: {
                    rgb: "000000",
                  },
                },
              };
            }
            // ----------------
            // SCORE COLORS
            markingTablesData.audit_cycle.forEach(
              (_, cycleIndex) => {

                const scoreMarks =
                  row.scores?.[cycleIndex]?.marks !==
                    undefined
                    ? row.scores[cycleIndex].marks
                    : null;

                const scoreCol =
                  3 + cycleIndex;

                const cellRef =
                  XLSX.utils.encode_cell({
                    r: currentRow,
                    c: scoreCol,
                  });

                if (
                  worksheet[cellRef] &&
                  scoreMarks !== null
                ) {
                  worksheet[cellRef].s = {
                    ...worksheet[cellRef].s,

                    fill: {
                      fgColor: {
                        rgb: getScoreColor(
                          scoreMarks,
                          row.max_marks
                        ),
                      },
                    },

                    font: {
                      bold: true,
                      color: {
                        rgb: "ffffff",
                      },
                    },
                  };
                }
              }
            );

            currentRow++;
          });

        currentRow += 2;
      });

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Store Performance"
    );

    XLSX.writeFile(
      workbook,
      "Store_Performance_Report.xlsx"
    );
  };
  return (
    <>
      {isClientAdmin ? (
        <div className="px-4 my-3 trends-container">
          <div className="quesnSele select gap-3 d-flex">
            <p className="text-start trendsQuesType">Questionnaire Type :</p>
            <select value={selectedOption} onChange={handleSelectChange}>
              {queTypesDataList?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div className="audit-details d-flex">
            <p className="ms-2">Store Performance</p>
          </div>
          <div className="dashSecHero justify-content-center align-items-center row d-flex">
            <div className="secHeroLeft col-sm-14">
              {isLoading ? (
                <div className="spinner">
                  <Spin size="smaller"></Spin>
                </div>
              ) : markingGraphData?.scores?.length > 0 ? (
                <div style={{ width: "100%", overflowX: "auto" }}>
                  <div style={{ minWidth: "600px" }}>
                    <BarChart
                      scores={markingGraphData?.scores}
                      auditCycle={markingGraphData?.audit_cycle}
                      maxMarks={markingGraphData?.max_marks}
                      colorCodes={markingGraphData?.color_codes}
                      isInteractive={true}
                      onBarClick={(index) => console.log("Bar clicked:", index)}
                    />{" "}
                  </div>{" "}
                </div>
              ) : (
                <div className="text-center py-4  noDataText">
                  No Store Performance data available
                </div>
              )}
            </div>
          </div>
          <div className="store-details">
            {/* <div className="audit-details d-flex"> */}
            <div className="audit-details">
              <p className="ms-2 mt-3" style={{ color: "#4B5563" }}>
                Store Performance Details
              </p>
              <button className="download-report-btn" onClick={handleDownloadReport} disabled={isLoading || tablesData?.length === 0} style={{ opacity: isLoading || tablesData?.length === 0 ? 0.5 : 1 }}>
                <Tooltip title="Export List">
                  <VerticalAlignBottomOutlined className="exportIconAT" />
                </Tooltip>
                Download Report
              </button>
            </div>
            {isLoading ? (
              <div className="spinner">
                <Spin size="medium" />
              </div>
            ) : tablesData?.length > 0 ? (
              tablesData
                .filter((table) =>
                  table.scores?.some((row) => row.max_marks !== 0)
                )
                .filter((table) =>
                  table.scores?.some(
                    (row) =>
                      row.scores?.length >=
                      markingTablesData?.audit_cycle?.length
                  )
                )
                .filter((table) =>
                  table.scores?.some(
                    (row) =>
                      (row.max_marks !== 0 ||
                        row.scores?.some((score) => score.marks > 0)) &&
                      row.scores?.length >=
                      markingTablesData?.audit_cycle?.length &&
                      !row.scores.every((score) => score.marks === null)
                  )
                )
                .map((table, tableIndex) => (
                  <div
                    key={tableIndex}
                    className={`table${tableIndex + 1} my-4`}
                  >
                    <div className="customerArr">
                      <p>{table.scores?.[0]?.section_name || "NA"}</p>
                    </div>
                    <div className="fifthTable table-responsive">
                      <table>
                        <thead>
                          <tr className="trends-row">
                            <th className="SNOTrends">Sr. no.</th>
                            <th className="QuesTrends">Questions</th>
                            <th className="maxMarksTrends">Max Marks</th>
                            {markingTablesData?.audit_cycle?.map(
                              (cycle, index) => (
                                <th key={index}>
                                  <div>
                                    <p className="my-2">{cycle}</p>
                                  </div>
                                </th>
                              )
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {table.scores
                            ?.filter(
                              (row) =>
                                row.max_marks !== 0 ||
                                row.scores?.some((score) => score.marks > 0)
                            )
                            .filter(
                              (row) =>
                                row.scores?.length >=
                                markingTablesData?.audit_cycle?.length
                            )
                            .filter(
                              (row) =>
                                !row.scores.every(
                                  (score) => score.marks === null
                                )
                            )
                            .map((row, rowIndex) => (
                              <tr
                                key={rowIndex}
                                style={{
                                  cursor: "pointer",
                                  backgroundColor:
                                    rowIndex % 2 === 0
                                      ? "#ffffff"
                                      : "rgb(242,242,242)",
                                  transition: "background-color 0.3s ease",
                                }}
                                onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  rowIndex % 2 === 0
                                    ? "rgb(242,242,242)"
                                    : "#ffffff")
                                }
                                onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  rowIndex % 2 === 0
                                    ? "#ffffff"
                                    : "rgb(242,242,242)")
                                }
                              >
                                <td className="SNOTrends">{row.sequence}</td>
                                <td className="text-start QuesTrends">
                                  {row.question_txt}
                                </td>
                                <td className="maxMarksTrends">
                                  <div className="maxMarksCenterText">
                                    {row.max_marks}
                                  </div>
                                </td>
                                {markingTablesData?.audit_cycle?.map(
                                  (cycle, cycleIndex) => {
                                    const scoreMarks =
                                      row.scores?.[cycleIndex]?.marks !==
                                        undefined
                                        ? row.scores[cycleIndex].marks
                                        : null;
                                    return (
                                      <td
                                        key={cycleIndex}
                                        className="competitionAudits"
                                      >
                                        <div className="scoreIcons dfTrends flex-column">
                                          <span>
                                            {scoreMarks !== null
                                              ? scoreMarks
                                              : "N/A"}
                                          </span>
                                          <div
                                            className="dash1Trends"
                                            style={{
                                              background: (() => {
                                                if (scoreMarks === null)
                                                  return "transparent";
                                                if (scoreMarks === 0)
                                                  return "#f1717d";
                                                if (
                                                  scoreMarks === row.max_marks
                                                )
                                                  return "#8DC63F";
                                                const percentage =
                                                  scoreMarks / row.max_marks;
                                                if (percentage >= 0.8)
                                                  return "rgb(195, 231, 112)";
                                                if (percentage >= 0.6)
                                                  return "#ffeb99";
                                                if (percentage >= 0.4)
                                                  return "rgb(255, 179, 128)";
                                                return "#ffa07a";
                                              })(),
                                            }}
                                          ></div>
                                        </div>
                                      </td>
                                    );
                                  }
                                )}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
            ) : (
              <div className="text-center p-4  noDataText">
                No Store Performance data available
              </div>
            )}
          </div>
          <Outlet />
        </div>
      ) : (
        <div className="trends-container">
          <Title level={3} className="textNonAdmin">
            No reports for this store only completed reports graph will show up
            here
          </Title>
        </div>
      )}
      {/* <StoreInsights /> */}
    </>
  );
};

export default Trends;
